<template>
  <div>
    <div
      v-if="pagination?.totalPages > 0"
      class="row-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="col">
        <p class="table-page-header">
          Items: {{ pagination.total }}, Pages: {{ pagination.totalPages }}
        </p>
      </div>
      <div>
        <l-pagination
          class="pagination-no-border"
          :value="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="pagination.total"
          @input="pageChange"
        />
      </div>
    </div>
    <div class="table-container">
      <table
        :class="['aiv-table', 'table-striped', { 'modal-table': modalTable }]"
      >
        <thead>
          <slot name="header">
            <tr>
              <th
                v-for="column in filteredColumns"
                :key="column.key"
                @click="column.sortable === false ? null : sortBy(column.key)"
                :class="{
                  'sorted-asc': sortKey === column.key && sortOrder === 'asc',
                  'sorted-desc': sortKey === column.key && sortOrder === 'desc',
                }"
              >
                {{ column.label }}
                <span v-if="sortKey === column.key">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
            </tr>
          </slot>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in sortedData"
            :key="index"
            :class="[
              rowClass ? rowClass(row) : '',
              { expanded: expandedRows[index] },
            ]"
          >
            <td :data-label="filteredColumns[0].label">
              <slot
                :name="`cell-${filteredColumns[0].key}`"
                :row="row"
                :column="filteredColumns[0]"
              >
                {{ row[filteredColumns[0].key] }}
              </slot>
              <button class="dropdown-toggle" @click="toggleRow(index)" />
            </td>
            <td
              v-for="column in filteredColumns.slice(1)"
              :key="column.key"
              :data-label="column.label"
            >
              <slot :name="`cell-${column.key}`" :row="row" :column="column">
                <div v-if="column.component">
                  <span v-if="column.componentProps(row).title">
                    {{ column.componentProps(row).title }}
                  </span>
                  <br v-if="column.componentProps(row).title" />
                  <component
                    v-if="column.component && column.component !== 'select'"
                    :is="column.component"
                    v-bind="
                      column.componentProps ? column.componentProps(row) : {}
                    "
                    @click="
                      column.componentProps
                        ? column.componentProps(row).onClick?.(row)
                        : null
                    "
                    component=""
                  />
                </div>
                <select
                  v-else-if="column.component === 'select'"
                  v-model="row[column.key]"
                  @change="(e) => (row[column.key] = e.target.value)"
                  v-select-min-width="column.componentProps(row).options"
                >
                  <option
                    v-for="option in column.componentProps(row).options"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
                <span
                  v-else-if="column.isHtml"
                  v-html="
                    column.value
                      ? column.value(row)
                      : column.format
                        ? column.format(row[column.key])
                        : row[column.key]
                  "
                ></span>
                <span v-else>
                  {{
                    column.format
                      ? column.format(getNestedValue(row, column.key))
                      : getNestedValue(row, column.key)
                  }}
                </span>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="pagination?.totalPages > 0"
      class="row-12 d-flex justify-content-center justify-content-sm-between flex-wrap mt-3"
    >
      <div class="col">
        <p class="table-page-header">
          Items: {{ pagination.total }}, Pages: {{ pagination.totalPages }}
        </p>
      </div>
      <div>
        <l-pagination
          class="pagination-no-border"
          :value="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="pagination.total"
          @input="pageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Pagination as LPagination } from "src/components/index";
import CommonAPIService from "src/serviceHandlers/CommonHandler";

const apiService = new CommonAPIService();

export default {
  components: {
    LPagination,
  },
  props: {
    columns: { type: Array, required: true },
    data: { type: Array, required: true },
    modalTable: { type: Boolean, default: false },
    rowClass: { type: Function, default: null },
    pagination: {
      type: Object,
      default: () => ({ total: 0, totalPages: 0, currentPage: 0, perPage: 0 }),
    },
    tableName: { type: String, default: "contacts" },
    formatData: { type: Function, default: null },
  },
  data() {
    return {
      sortKey: "",
      sortOrder: "asc",
      searchQuery: "",
      expandedRows: {},
    };
  },
  computed: {
    filteredColumns() {
      return this.columns.filter((column) => {
        if (typeof column.condition === "function") {
          return column.condition();
        }
        return true;
      });
    },
    sortedData() {
      return this.data;
    },
  },
  methods: {
    toggleRow(idx) {
      this.$set(this.expandedRows, idx, !this.expandedRows[idx]);
    },
    getNestedValue(obj, key) {
      return key.split(".").reduce((o, i) => (o ? o[i] : null), obj);
    },
    sortBy(key) {
      const column = this.filteredColumns.find((col) => col.key === key);
      if (column?.sortable === false) {
        return;
      }

      const sortField = column?.sortKey || key;
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
      this.$emit("sort-change", {
        prop: sortField,
        order: this.sortOrder,
      });
    },
    pageChange(newPage) {
      this.$emit("page-change", newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
  directives: {
    selectMinWidth: {
      inserted(el, binding) {
        const options = binding.value;
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.visibility = "hidden";
        tempDiv.style.height = "auto";
        tempDiv.style.width = "auto";
        tempDiv.style.whiteSpace = "nowrap";
        document.body.appendChild(tempDiv);

        let maxWidth = 0;
        options.forEach((option) => {
          tempDiv.textContent = option;
          const width = tempDiv.clientWidth;
          if (width > maxWidth) {
            maxWidth = width;
          }
        });

        document.body.removeChild(tempDiv);
        el.style.minWidth = `${maxWidth + 40}px`;
      },
    },
  },
};
</script>

<style scoped>
.dropdown-toggle {
  display: none;
}
.aiv-table {
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 10px;
  overflow-x: auto;
  background-color: white;
  color: black;
  border: 1px solid black;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}
.aiv-table th,
.aiv-table td {
  padding: 14px 10px;
  border: 1px solid black;
}
.aiv-table th {
  font-weight: 100;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  text-transform: uppercase;
  font-size: 16px;
  color: black;
  cursor: pointer;
  user-select: none;
}
.aiv-table th:hover {
  background-color: white;
}
.aiv-table th:first-child {
  border-top-left-radius: 8px;
}
.aiv-table th:last-child {
  border-top-right-radius: 8px;
}
.aiv-table tr:last-child td:first-child {
  border-bottom-left-radius: 8px;
}
.aiv-table tr:last-child td:last-child {
  border-bottom-right-radius: 8px;
}

@media (max-width: 768px) {
  .aiv-table thead {
    display: none;
  }

  .aiv-table,
  .aiv-table tbody {
    display: block;
    width: 100% !important;
  }

  .aiv-table td:first-child {
    display: flex;
    flex: 1 1 100%;
    justify-content: space-between;
    align-items: center;
  }

  .aiv-table tr {
    border-bottom: 2px solid black;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
    box-sizing: border-box;
  }

  .aiv-table td {
    text-align: left;
    position: relative;
    padding: 14px 10px !important;
    display: none;
  }

  .aiv-table .dropdown-toggle {
    display: inline-block;
    margin-left: 8px;
    background: none;
    border: 1px solid black;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.9em;
    cursor: pointer;
  }

  .aiv-table tr.expanded td:not(:first-child) {
    display: flex;
    flex: 1 1 33.33%;
    border: none;
    box-sizing: border-box;
    align-items: center;
    height: auto;
  }

  .aiv-table /deep/ td::before {
    content: attr(data-label);
    position: absolute;
    top: 0;
    left: 10px;
    white-space: nowrap;
    font-weight: bold;
    color: black;
    font-size: 10px;
  }
}
/*noinspection CssUnusedSymbol */
.modal-table {
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  background-color: white;
  color: black;
  border: 1px solid black;
}
.modal-table th,
.modal-table tr,
.modal-table td {
  color: black;
  background-color: white !important;
  border: 1px solid black;
}
.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.table-container::-webkit-scrollbar {
  height: 6px;
}
.table-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}
</style>
